import { useState, useEffect } from "react"
import { useNavigate, useSearchParams, useParams } from "react-router-dom"
import { Text } from "@/components/Primitives/Text"
import { Button } from "@/components/Primitives/Button"
import { useConfig } from "@/hooks/useConfig"
import { useStartJourney } from "@/hooks/useStartJourney"
import { LoadingState } from "@/components/Primitives/LoadingState"
import { useJourneys } from "@/hooks/useJourneys"
import { isAuthenticated } from "@/hooks/useLocalStorage"
import { QuestionAnswer } from "@/lib/api/quests"
import { Question as QuestionType } from "@/types/question"
import { toast } from "sonner"
import { Toaster } from "@/components/Primitives/Sonner"
import { getCircleQuestions } from "@/lib/api/circle"
import { answerQuestions } from "@/lib/api/question"
import { hasQuestionsAnswered } from "@/lib/utils/questions"
import { QuestionForm } from "@/components/Complex/QuestionForm"

export function CircleQuestionsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { circleId } = useParams<{ circleId: string }>()
  const questId = searchParams.get("quest_id")
  const { t } = useConfig()
  const { startJourney, isStarting } = useStartJourney(circleId!)
  const { journeys } = useJourneys(isAuthenticated())

  const [questions, setQuestions] = useState<QuestionType[]>([])
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [isLoading, setIsLoading] = useState(true)

  const handleNavigation = async (answers?: QuestionAnswer[]) => {
    const currentJourney = journeys.find((j) => j.quest.id === questId)
    let url: string

    if (questId && currentJourney) {
      url = `/quests/${questId}`
    } else if (questId) {
      await startJourney(questId, answers)
      url = `/quests/${questId}`
    } else {
      url = "/profile"
    }

    navigate(url)
  }

  useEffect(() => {
    ;(async () => {
      const questions = await getCircleQuestions(circleId!, questId)
      if (questId && hasQuestionsAnswered(questions)) {
        await handleNavigation()
        return
      } else {
        const backend_answers = questions.reduce(
          (acc: Record<string, number>, q) => {
            acc[q.question_code] = q.current_value
            return acc
          },
          {},
        )
        setAnswers(backend_answers)
      }

      const supportedQuestions = questions.filter((q) =>
        ["Choice", "Text"].includes(q.kind),
      )
      setQuestions(supportedQuestions)
      setIsLoading(false)
    })()
  }, [circleId, questId, journeys])

  const handleAnswerChange = (questionCode: string, value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [questionCode]: value,
    }))
  }

  const handleSubmit = async (submissionAnswers: Record<string, number>) => {
    const formattedAnswers: QuestionAnswer[] = Object.entries(
      submissionAnswers,
    ).map(([question_code, choice_value]) => ({
      question_code,
      choice_value,
    }))

    try {
      await answerQuestions(circleId!, formattedAnswers)
      await handleNavigation(formattedAnswers)
    } catch (error) {
      console.error("Error starting journey:", error)

      const errorToastStyle = "!bg-red-500 !text-white !border !border-red-500"
      toast.error(t("config_questions_error"), {
        className: errorToastStyle,
      })
    }
  }

  if (isLoading) {
    return <LoadingState message="Loading questions…" />
  }

  if (!questId || questions.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Text variant="lead">
          No question available for this quest or it doesn't exist.
        </Text>
        <Button onClick={() => navigate("/quests")} className="mt-4">
          Go to Quests
        </Button>
      </div>
    )
  }

  return (
    <>
      <QuestionForm
        questions={questions}
        answers={answers}
        onAnswerChange={handleAnswerChange}
        onSubmit={handleSubmit}
        isSubmitting={isStarting}
        showSkip={false}
        showProgress={true}
      />
      <Toaster position="top-center" />
    </>
  )
}
